import React, { useCallback, useEffect, useState } from "react";
import { TournamentEntryList } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./PendingGameEntriesSection.scss";
import PendingGameCard from "../../PendingGameCard/PendingGameCard";
import TooltipButton from "../../TooltipButton/TooltipButton";
import PendingGamesInfoModal from "../../modals/PendingGamesInfoModal/PendingGamesInfoModal";
import { handleGetTournamentPendingEntries } from "../../../../api-clients/tournamentsClient";
import Skeleton from "../../../Skeleton/Skeleton";
import SkeletonList from "../../../SkeletonList/SkeletonList";
import { ThemeVariant } from "../../../../utils/constants/generic-types";

const SKELETON_SHIMMER_STYLE = { background: "#1b0c3dff" };
const SKELETON_BG_STYLE = { background: "rgba(51, 23, 120, 0.1)" };

function LoadingSkeleton() {
  return (
    <section className="pending-game-entries-section pending-game-entries-loading">
      <div className="heading-container px-3">
        <Skeleton
          className="pending-game-heading-skeleton"
          height={18}
          width={128}
          borderRadius={4}
          shimmerStyle={SKELETON_SHIMMER_STYLE}
          style={SKELETON_BG_STYLE}
          theme={ThemeVariant.DARK}
        />
      </div>
      <div className="d-flex gap-2 px-3 cards-container scrollbar-hidden">
        <div className="pending-game-card pending-game-card-skeleton">
          <div className="d-flex gap-2 content">
            <Skeleton
              width={44}
              height={44}
              borderRadius={8}
              shimmerStyle={SKELETON_SHIMMER_STYLE}
              style={SKELETON_BG_STYLE}
              theme={ThemeVariant.DARK}
            />
            <div className="w-100 d-flex flex-column justify-content-center">
              <Skeleton
                width={120}
                height={16}
                borderRadius={4}
                shimmerStyle={SKELETON_SHIMMER_STYLE}
                style={SKELETON_BG_STYLE}
                theme={ThemeVariant.DARK}
              />
              <Skeleton
                className="pending-game-meta-skeleton"
                width={160}
                height={12}
                borderRadius={4}
                shimmerStyle={SKELETON_SHIMMER_STYLE}
                style={SKELETON_BG_STYLE}
                theme={ThemeVariant.DARK}
              />
            </div>
          </div>
          <div className="d-flex justify-content-between gap-3 btn-box">
            <SkeletonList noOfItems={2}>
              <Skeleton
                className="pending-game-btn-skeleton"
                height={40}
                width={138}
                borderRadius={100}
                shimmerStyle={SKELETON_SHIMMER_STYLE}
                style={SKELETON_BG_STYLE}
                theme={ThemeVariant.DARK}
              />
            </SkeletonList>
          </div>
        </div>
      </div>
    </section>
  );
}

function PendingGameEntriesSection() {
  const { t } = useTranslation("dashboard");
  const [isOpen, setIsOpen] = useState(false);
  const [pendingGames, setPendingGames] = useState<TournamentEntryList>();
  const [isLoading, setIsLoading] = useState(true);

  const getPendingGames = useCallback(async () => {
    const games = await handleGetTournamentPendingEntries();
    setPendingGames(games);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    getPendingGames();
  }, [getPendingGames]);

  if (isLoading) return <LoadingSkeleton />;

  if (!pendingGames || pendingGames.records.length === 0) return null;

  return (
    <section
      className={`pending-game-entries-section ${isLoading ? "px-3" : ""}`}
    >
      <div className="heading-container d-flex justify-content-between align-items-center px-3">
        <h2 className="heading">{t("pending_game")}</h2>
        <TooltipButton onClick={() => setIsOpen(true)} size={18} />
      </div>
      <div className="d-flex gap-2 px-3 cards-container scrollbar-hidden">
        {pendingGames?.records.map((game) => (
          <PendingGameCard key={game.id} {...game} refetch={getPendingGames} />
        ))}
      </div>

      <PendingGamesInfoModal show={isOpen} onHide={() => setIsOpen(false)} />
    </section>
  );
}

export default PendingGameEntriesSection;
